import { Button, Icon, Loader, Modal, Spinner, useModal } from '@afterpaytouch/core'
import React, { FunctionComponent, useEffect } from 'react'
import { useAppDispatch, useCheckoutToken, useIsApplePaylater, useLazyGetCheckoutPpaDocumentPDFQuery, useLazyGetCheckoutPpaDocumentQuery } from '../../state'
import styles from './style.module.css'
import { TruthInLendingIframe } from './TruthInLendingIframe'
import { PayMonthlyTILAModalKey } from '../../model/modalKey'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { isInAppBrowser } from '../../utils/device'
import { downloadOrSharePdf } from '../../utils/downloadPdf'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model/features'

interface TruthInLendingModalProps {
  documentId: string
  isTilDisclosure: boolean
}

export const TruthInLendingModal: FunctionComponent<TruthInLendingModalProps> = ({ documentId, isTilDisclosure }) => {
  const { visible, closeModal } = useModal(PayMonthlyTILAModalKey)
  const [getDocument, { data: documentData, isFetching: lazyDocumentIsLoading }] = useLazyGetCheckoutPpaDocumentQuery()
  const [getDocumentPDF, { isLoading: isPDFLoading }] = useLazyGetCheckoutPpaDocumentPDFQuery()
  const dispatch = useAppDispatch()
  const token = useCheckoutToken()
  const isApplePaylater = useIsApplePaylater()
  const isDownloadEnabled = useFlag(Features.CHECKOUT_ENABLE_DOWNLOAD_PPA_PDF_API)
  // window.print() on iframe content does not work in any in-app browser. Apple Pay Later's
  // WebView isn't detected by the UA-based isInAppBrowser() (it's identified via merchant/cookie),
  // so include it explicitly. In these contexts we offer a PDF download and never show print.
  const isInAppContext = isInAppBrowser() || isApplePaylater
  const showDownload = isInAppContext && isDownloadEnabled

  useEffect(() => {
    if (!visible || documentId.trim().length === 0) {
      return
    }
    dispatch(async () => await getDocument({ token, docId: documentId }))
  }, [visible, documentId])

  const printDocument = (): void => {
    const documentIframe: HTMLIFrameElement | null = document.querySelector('#document-window')
    if (documentIframe === null || documentIframe.contentWindow === null) {
      return
    }
    documentIframe.contentWindow.print()
  }

  const downloadDocument = async (): Promise<void> => {
    if (documentId.trim().length === 0) {
      return
    }

    try {
      const result = await getDocumentPDF({ token, docId: documentId }).unwrap()
      const blob = new Blob([result], { type: 'application/pdf' })
      await downloadOrSharePdf(blob, 'truth-in-lending-disclosure.pdf')
    } catch (error) {
      capture.error('til download error', { ...error })
    }
  }

  return (
    <Modal
      id={PayMonthlyTILAModalKey}
      show={visible}
      onBackdropClick={closeModal}
      showCloseButton={visible}
      testNameSpace='til-document-modal'
      renderMode={isApplePaylater ? 'Native' : undefined}
    >
      <div className={styles.iframeModalBtn}>
        {showDownload ? (
          <Button.Link type='button' aria-label='til-download-document-button' onClick={downloadDocument} disabled={isPDFLoading}>
            {isPDFLoading ? <Spinner size='24' /> : <Icon.Download size='Medium' color='Black' />}
          </Button.Link>
        ) : (
          !isInAppContext && (
            <Button.Link type='button' aria-label='til-print-document-button' onClick={printDocument} disabled={lazyDocumentIsLoading}>
              <Icon.Printer size='Medium' color='Black' />
            </Button.Link>
          )
        )}
        <Button.Link type='button' aria-label='til-close-modal-button' onClick={closeModal}>
          <Icon.Close size='Medium' color='Black' />
        </Button.Link>
      </div>
      {lazyDocumentIsLoading ? (
        <Loader />
      ) : (
        <TruthInLendingIframe documentData={documentData} isTilDisclosure={isTilDisclosure} isApplePaylater={isApplePaylater} isInAppBrowser={isInAppContext} />
      )}
    </Modal>
  )
}
